<template>
  <div>
    <section id="filters">
      <vs-divider position="left">Where</vs-divider>
      <spatial-filter :force-active="true" />
    </section>
    <vs-divider position="left">Specie</vs-divider>
    <div class="inner-container">
      <div class="specie-container">
        <vs-select v-model="selectedSpecie" class="species-combo">
          <vs-select-item
            v-for="(specie, index) in species"
            :key="index"
            :value="specie"
            :text="specie.name"
          />
        </vs-select>
        <vs-input
          v-if="isCustomSpecie"
          v-model="specieName"
          icon="local_offer"
          label-placeholder="Specie Name"
          size="small"
          class="inputs-sctool"
        />
      </div>
      <div class="variables-container">
        <div class="sliders-container">
          <span class="variable-label label">Salinity</span>
          <vs-slider
            v-model="selectedSpecie.salinity"
            :color="secondary"
            :min="salinityRanges.min"
            :max="salinityRanges.max"
            :disabled="!isCustomSpecie"
            text-fixed="psu"
            :step-decimals="true"
            step="0.1"
          />
          <div class="variable-label label">
            <span>
              {{ selectedSpecie.salinity[0] }} to
              {{ selectedSpecie.salinity[1] }} (psu)
            </span>
          </div>
        </div>
        <div class="sliders-container">
          <span class="variable-label label">Temperature</span>
          <vs-slider
            v-model="selectedSpecie.temperature"
            :color="secondary"
            :min="temperatureRanges.min"
            :max="temperatureRanges.max"
            :disabled="!isCustomSpecie"
            text-fixed="°"
            :step-decimals="true"
            step="0.1"
          />
          <div class="variable-label label">
            <span>
              {{ selectedSpecie.temperature[0] }}° to
              {{ selectedSpecie.temperature[1] }}°
            </span>
          </div>
        </div>
      </div>
    </div>
    <vs-divider position="left">Period</vs-divider>
    <div class="inner-container">
      <ul class="radio-opts label">
        <li>
          <vs-radio v-model="period" :color="secondary" vs-value="5"
            >Last 5 years*</vs-radio
          >
        </li>
        <li>
          <vs-radio v-model="period" :color="secondary" vs-value="10"
            >Last 10 years*</vs-radio
          >
        </li>
        <li>
          <vs-radio v-model="period" :color="secondary" vs-value="15"
            >Last 15 years*</vs-radio
          >
        </li>
        <li>
          <vs-radio v-model="period" :color="secondary" vs-value="20"
            >Last 20 years*</vs-radio
          >
        </li>
        <li>
          <vs-radio v-model="period" :color="secondary" vs-value="0"
            >All serie*</vs-radio
          >
        </li>
      </ul>
      <div class="years-container">
        <span class="label-years">*Years: {{ selectedPeriod.label }}</span>
      </div>
    </div>
    <div class="footer-panel">
      <vs-button
        :color="secondary"
        type="filled"
        icon="timeline"
        @click="
          isFormValid
            ? runHistorical()
            : $_utilsMixin_notify(
                'Invalid Inputs',
                'Check input values and try again',
                2
              )
        "
        >Load Historical Data</vs-button
      >
      <vs-button
        :color="secondary"
        type="filled"
        icon="dns"
        @click="
          isFormValid
            ? runBiological()
            : $_utilsMixin_notify(
                'Invalid Inputs',
                'Check form before running calculations',
                2
              )
        "
        >Calculate Biological Suitability</vs-button
      >
    </div>
  </div>
</template>
<script>
import SpatialFilterControl from "@/components/ui/SpatialFilterControl";
import species from "@/../static/data/SCTool/species.json";
import { MSPTOOLS_API } from "@/config/apis";
import { BTM_PANELS } from "@/constants.js";
import { loadHistoricalData, getBiologicalSuitability } from "@/api/tools";
import { mapState, mapActions } from "vuex";

export default {
  components: {
    "spatial-filter": SpatialFilterControl
  },
  data() {
    return {
      bioToolConfig: MSPTOOLS_API.biological,
      secondary: "#19535F",
      specieName: "",
      period: 5,
      salinityRanges: { min: 0, max: 40 },
      temperatureRanges: { min: 0, max: 40 },
      selectedSpecie: {},
      selectedPeriod: {},
      species: species
    };
  },
  computed: {
    ...mapState({
      loading: state => state.loading
    }),
    ...mapState("map", {
      marker: state => state.marker
    }),
    isCustomSpecie() {
      return this.selectedSpecie
        ? this.selectedSpecie.name === "Custom"
        : false;
    },
    isLonValid() {
      return this.marker.lon < 180 && this.marker.lon > -180 && this.marker.lon;
    },
    isLatValid() {
      return this.marker.lat < 90 && this.marker.lat > -90 && this.marker.lat;
    },
    isFormValid() {
      return this.isLonValid && this.isLatValid;
    }
  },
  watch: {
    period() {
      switch (this.period) {
        case "5":
          this.setPeriod(5);
          break;
        case "10":
          this.setPeriod(10);
          break;
        case "15":
          this.setPeriod(15);
          break;
        case "20":
          this.setPeriod(20);
          break;
        default:
          this.selectedPeriod = {
            ini: `${this.bioToolConfig.years[0]}-01-01`,
            end: `${this.bioToolConfig.years[1]}-01-01`,
            label: `${this.bioToolConfig.years[0]}-${
              this.bioToolConfig.years[1]
            }`
          };
          break;
      }
    }
  },
  created() {
    // Form initialization
    this.species = species;
    this.selectedSpecie = this.species[0];
    this.selectedPeriod = {
      ini: `${this.bioToolConfig.years[1] - this.period}-01-01`,
      end: `${this.bioToolConfig.years[1]}-01-01`,
      label: `${this.bioToolConfig.years[1] - this.period}-${
        this.bioToolConfig.years[1]
      }`
    };
  },
  methods: {
    ...mapActions({
      toggleLoading: "toggleLoading",
      updateBiologicalResult: "updateBiologicalResult",
      updateHindcastDataResults: "updateHindcastDataResults",
      setBTMToolsActivePanel: "setBTMToolsActivePanel"
    }),
    ...mapActions("map", {
      setScToolPoint: "setScToolPoint"
    }),
    setPeriod(years) {
      this.selectedPeriod = {
        ini: `${MSPTOOLS_API.biological.years[1] - years}-01-01`,
        end: `${MSPTOOLS_API.biological.years[1]}-01-01`,
        label: `${MSPTOOLS_API.biological.years[1] - years}-${
          MSPTOOLS_API.biological.years[1]
        }`
      };
    },
    runHistorical() {
      this.$vs.dialog({
        type: "confirm",
        color: "warning",
        title: `Warning`,
        text: "This query might take several minutes, do you want to continue?",
        accept: this.loadHistoricalData
      });
    },
    loadHistoricalData() {
      const parameters = this.setupParameters();

      this.toggleLoading(true);
      loadHistoricalData(parameters)
        .then(res => {
          if (res.data.status !== "OK") {
            this.$_utilsMixin_notify("Warning", res.data.msg, 1);
            return;
          }
          const result = {
            params: {
              point: JSON.parse(parameters).point,
              specie: this.selectedSpecie
            },
            data: res.data.value
          };
          this.updateHindcastDataResults(result);
          this.setBTMToolsActivePanel(BTM_PANELS.TOOLS.VIEWER);
          this.$_utilsMixin_notify(
            "Data loaded",
            "Historical data loaded successfully",
            0
          );
        })
        .catch(error => {
          this.$_utilsMixin_notify("Error", error, 2);
        })
        .finally(() => {
          this.toggleLoading(false);
        });
    },
    runBiological() {
      this.$vs.dialog({
        type: "confirm",
        color: "warning",
        title: `Warning`,
        text: "This query might take several minutes, do you want to continue?",
        accept: this.getBiologicalSuitability
      });
    },
    getBiologicalSuitability() {
      const parameters = this.setupParameters();

      this.toggleLoading(true);
      getBiologicalSuitability(parameters)
        .then(res => {
          if (res.data.status !== "OK") {
            this.$_utilsMixin_notify("Warning", res.data.msg, 1);
            return;
          }
          this.updateBiologicalResult({
            params: JSON.parse(parameters),
            index: res.data.value
          });
          this.setBTMToolsActivePanel(BTM_PANELS.TOOLS.RESULTS);
          this.$_utilsMixin_notify(
            "Process complete",
            "Script executed successfully",
            0
          );
        })
        .catch(error => {
          this.$_utilsMixin_notify("Error", error, 2);
        })
        .finally(() => {
          this.toggleLoading(false);
        });
    },
    setupParameters() {
      return JSON.stringify({
        point: { lon: this.marker.lon, lat: this.marker.lat },
        specie: {
          name: this.isCustomSpecie
            ? this.specieName
            : this.selectedSpecie.name,
          salinity_min: this.selectedSpecie.salinity[0],
          salinity_max: this.selectedSpecie.salinity[1],
          temperature_min: this.selectedSpecie.temperature[0],
          temperature_max: this.selectedSpecie.temperature[1]
        },
        dates: {
          ini: this.selectedPeriod.ini,
          end: this.selectedPeriod.end
        }
      });
    }
  }
};
</script>
<style scoped>
.inner-container {
  display: flex;
  flex-grow: 1;
  justify-content: flex-start;
  padding-left: 1rem;
}

.radio-opts {
  flex-direction: row;
  display: flex;
  justify-content: space-between;
  flex-grow: 1;
}

.radio-opts li {
  padding: 10px;
  margin-left: 10px;
  border-radius: 5px;
  list-style: none;
}

.footer-panel {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
  margin-bottom: 1rem;
}
.footer-panel button {
  margin-right: 1rem;
}

.inputs-sctool {
  margin-left: 10px;
  margin-right: 10px;
}

.inputs-sctool >>> .vs-input--icon {
  font-size: 0.8rem;
  top: 6px;
}

.species-container {
  flex-grow: 0.5;
}

.species-combo {
  margin: 10px;
}

.variables-container {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  padding-left: 2rem;
  padding-right: 2rem;
  padding-top: 5px;
}

.variable-label {
  min-width: 100px;
  text-align: left;
}

.sliders-container {
  display: inline-flex;
  align-items: center;
  font-size: 0.8rem;
}
.con-vs-slider {
  margin-left: 20px;
  margin-right: 20px;
  min-width: 100px;
}

.years-container {
  display: flex;
  flex-grow: 0.8;
  flex-direction: row;
  justify-content: flex-end;
}
.label-years {
  text-align: left;
  padding-left: 2rem;
  font-size: 0.8rem;
}
.top-container {
  justify-content: center;
  display: flex;
  margin-top: 5px;
}
</style>
